import { useEffect, useState, useMemo, useRef, type ReactElement, useContext } from 'react'
import { Typography, Box, Grid, Select, MenuItem, ListSubheader, Button, Input } from '@mui/material'
import { ZERO_ADDRESS } from '@safe-global/safe-core-sdk/dist/src/utils/constants'
import classnames from 'classnames'
import useDesktop from '@/hooks/useDesktop'
import css from './styles.module.css'
import useBalances from '@/hooks/useBalances'
import TokenIcon from '@/components/common/TokenIcon'
import { formatVisualAmount } from '@/utils/formatters'
import { isChecksummedAddress } from '@/utils/addresses'
import { validateLimitedAmount } from '@/utils/validation'
import EthHashInfo from '@/components/common/EthHashInfo'
import { createTokenTransferParams } from '@/services/tx/tokenTransferParams'
import TransactionLayout from '@/components/common/TransactionLayout/TransactionLayout'
import { ModalButtonContext } from '@/components/common/ModalButton'
import ChainIndicator from '@/components/common/ChainIndicator'
import { PendingStatus } from '@/store/pendingTxsSlice'
import TextInputComponent from '@/components/common/TextInputComponent'
import { SendProcessingContext } from '@/components/inheritors/SendProcessing'
import { shortenText, safeParseUnits } from '@/utils/formatters'
import { NumericFormat } from 'react-number-format'
import { useCurrentChain } from '@/hooks/useChains'
import FeeSection from '@/components/common/FeeSection'
import AmountWithIcon from '@/components/common/AmountWithIcon'
import type { MetaTransactionData } from '@safe-global/safe-core-sdk-types' //TransactionOptions

const SendAssets = (): ReactElement => {
  const { isSending, sendTx, sendError, txStatus, setSendError } = useContext(SendProcessingContext)
  const { setModalOpen, setTitle } = useContext(ModalButtonContext)
  const { balances, loading } = useBalances()

  const [selectedToken, setSelectedToken] = useState(0)

  // local states
  const [step, setStep] = useState(0) // 0 = input; 1 = confirm; 2 = sending
  const [address, setAddress] = useState('')
  const [addressError, setAddressError] = useState('')
  const [amount, setAmount] = useState('')
  const [amountError, setAmountError] = useState('')
  const [canSend, setCanSend] = useState(false)
  const [metaTxData, setMetaTxData] = useState<MetaTransactionData | undefined>(undefined)
  const [fee, setFee] = useState('')

  const ref = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  const isDesktop = useDesktop()
  const chainInfo = useCurrentChain()

  const tokenList = useMemo(() => {
    if (balances.items.length === 0 && chainInfo) {
      const nativeCurrency = chainInfo.nativeCurrency
      return [
        {
          logo: <TokenIcon logoUri={nativeCurrency.logoUri} size={20} tokenSymbol={nativeCurrency.symbol} />,
          balance: '0',
          symbol: nativeCurrency.symbol,
          name: nativeCurrency.name,
          decimals: nativeCurrency.decimals,
          logoUri: nativeCurrency.logoUri,
          address: ZERO_ADDRESS,
        },
      ]
    }

    return balances.items.map((token, index) => {
      return {
        logo: <TokenIcon logoUri={token.tokenInfo.logoUri} size={20} tokenSymbol={token.tokenInfo.symbol} />,
        balance: formatVisualAmount(token.balance, token.tokenInfo.decimals, 6),
        // balance: token.balance,
        symbol: token.tokenInfo.symbol,
        name: token.tokenInfo.name,
        decimals: token.tokenInfo.decimals,
        logoUri: token.tokenInfo.logoUri,
        address: token.tokenInfo.address,
      }
    })
  }, [balances])

  useEffect(() => {
    if (txStatus && txStatus === PendingStatus.INDEXING) {
      setAmount('')
      setAmountError('')
      setAddressError('')
      setAddress('')
      setCanSend(false)
      setStep(0)
      return
    }
  }, [setModalOpen, txStatus])

  useEffect(() => {
    // const amountNumber = parseFloat(removeComma(amount))
    if (amount !== '') {
      if (amount === '0') {
        setAmountError('The value must be greater than 0')
        return
      }
      const isNativeToken = parseInt(tokenList[selectedToken].address, 16) === 0
      let error: string | undefined = undefined

      if (isNativeToken) {
        error = validateLimitedAmount(
          (parseFloat(amount) + parseFloat(fee)).toString(10),
          tokenList[selectedToken].decimals,
          balances.items[selectedToken]?.balance || '0',
        )
      } else {
        error = validateLimitedAmount(
          amount,
          tokenList[selectedToken].decimals,
          balances.items[selectedToken]?.balance || '0',
        )
      }

      if (error) {
        setAmountError(error)
      } else {
        setAmountError('')
      }
    }

    if (address !== '' && addressError === '' && amount !== '' && amountError === '' && fee !== '') {
      setCanSend(true)
    } else {
      setCanSend(false)
    }
  }, [
    amount,
    address,
    addressError,
    amountError,
    sendError,
    selectedToken,
    balances.items,
    setSendError,
    tokenList,
    fee,
  ])

  useEffect(() => {
    // validate fee
    if (fee !== '') {
      const token = balances.items[selectedToken]
      const nativeBalance = balances.items.find((item) => parseInt(item.tokenInfo.address, 16) === 0)
      if (!nativeBalance) {
        setSendError('Insufficient balance to pay the fee')
        return
      }
      if (safeParseUnits(fee, nativeBalance.tokenInfo.decimals)?.gt(nativeBalance.balance)) {
        setSendError('Insufficient balance to pay the fee')
        return
      }
    }
  }, [balances.items, fee, selectedToken, setSendError])

  useEffect(() => {
    if (step === 0) {
      setTitle('Send assets')
      return
    }
    if (step === 1) {
      setTitle('Confirm sending')
      return
    }
  }, [setTitle, step])

  useEffect(() => {
    if (sendError && sendError !== '') {
      setStep(0)
    }
  }, [sendError])

  // use to estimate the gas fee
  useEffect(() => {
    const amount = '0'
    const token = balances.items[selectedToken]
    const prams = {
      recipient: ZERO_ADDRESS,
      amount,
      tokenAddress: token.tokenInfo.address,
      tokenDecimals: token.tokenInfo.decimals,
    }
    const metaTxData = createTokenTransferParams(prams.recipient, prams.amount, prams.tokenDecimals, prams.tokenAddress)
    setMetaTxData(metaTxData)
  }, [selectedToken, balances])

  const createSafeTx = () => {
    const token = balances.items[selectedToken]
    const prams = {
      recipient: address,
      amount,
      tokenAddress: token.tokenInfo.address,
      tokenDecimals: token.tokenInfo.decimals,
    }
    return createTokenTransferParams(prams.recipient, prams.amount, prams.tokenDecimals, prams.tokenAddress)
  }

  const handleSend = async () => {
    setSendError('')
    const txParams = createSafeTx()

    if (step === 1) {
      void sendTx(txParams)
    }
    setStep(step + 1)
  }

  const handleBack = () => {
    if (step === 0) {
      setModalOpen(false)
      return
    }
    setStep(step - 1)
  }

  const handleMaxClick = () => {
    const balance = tokenList[selectedToken].balance
    const isNativeToken = parseInt(tokenList[selectedToken].address, 16) === 0
    if (isNativeToken) {
      if (fee === '') {
        return
      }
      const max = parseFloat(balance) - parseFloat(fee) - 0.00001
      if (max > 0) {
        setAmount(max.toString())
      }
      return
    }
    setAmount(balance)
  }

  const handleFeeChange = ({ feeType, feeAmount }: { feeType: string; feeAmount: string }) => {
    setFee(feeAmount)
  }

  const handleFeeError = (error: string) => {
    console.log('error', error)
  }

  // const formatCurrency = (value: string): string => {
  //   const number = parseFloat(value)
  //   if (isNaN(number)) {
  //     return value
  //   }
  //   return number.toLocaleString(undefined, { maximumFractionDigits: 20 })
  // }

  // const removeComma = (value: string): string => {
  //   return value.replace(/,/g, '')
  // }

  // const isNumber = (value: string): boolean => {
  //   const regex = /^\d+$/
  //   return regex.test(value)
  // }

  // const handleAmountChange = (event: any) => {
  //   const value = removeComma(event.target.value)
  //   const splittedValue = value.split('.')

  //   if (splittedValue[0] && splittedValue[0] !== '') {
  //     if (!isNumber(splittedValue[0])) {
  //       setAmount('0')
  //       return
  //     }
  //   }

  //   if (splittedValue[1] && splittedValue[1] !== '') {
  //     if (!isNumber(splittedValue[1])) {
  //       setAmount('0')
  //       return
  //     }
  //   }

  //   const formated =
  //     splittedValue[1] === undefined
  //       ? formatCurrency(splittedValue[0])
  //       : splittedValue[1] === ''
  //       ? formatCurrency(splittedValue[0]) + '.'
  //       : formatCurrency(splittedValue[0]) + '.' + splittedValue[1]

  //   setAmount(formated)
  // }

  const handleAmountChange = (values: any, source: any) => {
    setAmount(values.value)
  }

  const onChange = (event: any) => {
    setSelectedToken(event.target.value)
  }

  const handleAddressChange = (id: string, value: string) => {
    // const value = event.target.value
    setAddress(value)

    if (value === '') {
      setAddressError('Address is required')
    } else if (!isChecksummedAddress(value)) {
      setAddressError('Invalid address format')
    } else {
      setAddressError('')
    }
  }

  return (
    <TransactionLayout>
      <Box pt={'24px'}>
        <Box display="flex" flexDirection="column" flexWrap="wrap" justifyContent="center" alignItems="center">
          <Grid container>
            <Grid item xs={12}>
              <ConfirmComponent
                isShow={step === 1}
                recipient={address}
                amount={amount}
                logoUri={tokenList[selectedToken].logoUri}
                symbol={tokenList[selectedToken].symbol}
              />
              {step === 0 && (
                <Grid
                  container
                  sx={{
                    paddingX: isDesktop ? '16px' : '0px',
                    paddingY: isDesktop ? '16px' : '0px',
                    width: 'auto',
                    border: isDesktop ? '1px solid var(--Ebony-Clay-950, #2F354B)' : 'none',
                    borderRadius: '6px',
                  }}
                >
                  <TextInputComponent
                    id="address"
                    label="Recipient address"
                    labelBold={true}
                    value={address}
                    onChange={handleAddressChange}
                    errorMessages={addressError}
                    showQRButton={true}
                    // prefixNetwork={true}
                  />
                  <Grid mt="8px" item container xs={6} flexDirection="column" justifyContent="center">
                    <Typography
                      textAlign="left"
                      sx={{
                        color: 'var(--Ebony-Clay-400, #AAB3CF)',
                        fontSize: '14px',
                        lineHeight: '160%',
                        fontWeight: '600',
                      }}
                    >
                      Amount
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Button
                      variant="text"
                      sx={{
                        color: '#FFF',
                        fontSize: '12px',
                        border: '1px solid var(--Ebony-Clay-500, #7C8AB4)',
                        borderRadius: '2px',
                        padding: '4px 8px',
                        minWidth: 'auto',
                      }}
                      onClick={handleMaxClick}
                    >
                      Max
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    ref={(el) => {
                      const width = el && (el as HTMLElement).offsetWidth ? (el as HTMLElement).offsetWidth : 0
                      if (width > 0) {
                        setWidth(width)
                      }
                      ref.current = el
                    }}
                  >
                    <Grid
                      container
                      mt={'4px'}
                      mb={'6px'}
                      sx={{
                        borderRadius: '4px',
                        border: amountError !== '' ? '1px solid var(--Supporting-Carnation, #FF2239)' : 'none',
                      }}
                    >
                      <Grid item xs={'auto'}>
                        <Select
                          labelId="demo-customized-select-label"
                          id="demo-customized-select"
                          value={selectedToken}
                          onChange={onChange}
                          disableUnderline
                          variant="standard"
                          MenuProps={{
                            transitionDuration: 300,
                            sx: {
                              '>.MuiBackdrop-root': {
                                backgroundColor: 'transparent',
                              },
                              '>.MuiPaper-root': {
                                width,
                                backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
                                boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.50)',
                                marginTop: '4px',
                                overflow: 'auto',
                                maxHeight: '300px',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'none',
                              },
                            },
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                          }}
                          sx={{
                            borderRadius: '4px 0px 0px 4px',
                            backgroundColor: 'var(--Ebony-Clay-950, #2F354B)',
                            textAlign: 'center',
                            '& .MuiSelect-select': {
                              paddingY: '12px',
                              paddingLeft: '16px',
                            },
                          }}
                          renderValue={(value: any) => {
                            return (
                              <span className={classnames(css.inlineIndicator, css.withLogo)}>
                                {tokenList[value].logo} {shortenText(tokenList[value].symbol, 6)}
                              </span>
                            )
                          }}
                        >
                          <ListSubheader sx={{ backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)' }}>
                            <Grid container>
                              <Grid item xs={6}>
                                Assets
                              </Grid>
                              <Grid item xs={6} textAlign="right">
                                Balance
                              </Grid>
                            </Grid>
                          </ListSubheader>
                          {tokenList.map((token, index) => {
                            return (
                              <MenuItem key={`token_${index}`} value={index}>
                                <Grid
                                  container
                                  sx={{
                                    color: 'var(--Ebony-Clay-300, #D2D7E5)',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '160%',
                                    marginBottom: '8px',
                                  }}
                                >
                                  <Grid item xs={6}>
                                    <span className={classnames(css.inlineIndicator, css.withLogo)}>
                                      {token.logo}{' '}
                                      {isDesktop ? shortenText(token.name, 40) : shortenText(token.name, 10)}
                                    </span>
                                  </Grid>
                                  <Grid item xs={6} textAlign="right">
                                    {token.balance} {shortenText(token.symbol, 6)}
                                  </Grid>
                                </Grid>
                              </MenuItem>
                            )
                          })}
                        </Select>
                      </Grid>
                      <Grid item xs>
                        <NumericFormat
                          disableUnderline
                          customInput={Input}
                          thousandSeparator=","
                          decimalSeparator="."
                          value={amount}
                          // onChange={handleAmountChange}
                          onValueChange={handleAmountChange}
                          placeholder="0"
                          sx={{
                            width: '100%',
                            borderRadius: '0px 4px 4px 0px',
                            backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
                            padding: '9px 16px',
                            fontSize: '14px',
                            color: 'var(--Ebony-Clay-500, #7C8AB4)',
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      textAlign="left"
                      sx={{
                        color: 'var(--Ebony-Clay-500, #7C8AB4)',
                        fontSize: '14px',
                        lineHeight: '160%',
                        fontWeight: '400',
                      }}
                    >
                      Balance:{' '}
                      <span
                        style={{
                          color: 'var(--Ebony-Clay-300, #D2D7E5)',
                        }}
                      >
                        {tokenList[selectedToken].balance} {shortenText(tokenList[selectedToken].symbol, 6)}
                      </span>
                    </Typography>
                    {amountError !== '' && (
                      <Typography
                        textAlign="left"
                        sx={{
                          color: 'var(--Supporting-Carnation, #FF2239)',
                          fontSize: '14px',
                          lineHeight: '160%',
                          fontWeight: '400',
                        }}
                      >
                        {amountError}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
            <FeeSection metaTxData={metaTxData} onError={handleFeeError} onFee={handleFeeChange} isShowError={false} />

            {sendError !== '' && (
              <Typography
                textAlign="left"
                sx={{
                  color: 'var(--Supporting-Carnation, #FF2239)',
                  fontSize: '14px',
                  lineHeight: '160%',
                  fontWeight: '400',
                  marginTop: '8px',
                }}
              >
                {sendError}
              </Typography>
            )}
            <Grid container justifyContent="space-between" mt="32px" mb="16px">
              <Grid item xs={5}>
                <Button
                  disabled={step === 2}
                  variant="contained"
                  sx={{
                    width: '100%',
                    borderRadius: '4px',
                    background: 'var(--Ebony-Clay-950, #2F354B)',
                    color: 'var(--Ebony-Clay-300, #D2D7E5)',
                    ':hover': {
                      background: 'rgba(255, 255, 255, 0.08)',
                    },
                  }}
                  onClick={handleBack}
                >
                  {step === 0 ? 'Cancel' : 'Back'}
                </Button>
              </Grid>

              <Grid item xs={5}>
                <Button
                  disabled={!canSend || step === 2}
                  variant="contained"
                  sx={{ width: isDesktop ? '100%' : '100%' }}
                  onClick={handleSend}
                >
                  {step === 0 ? 'Send' : 'Confirm'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </TransactionLayout>
  )
}

const ConfirmComponent = ({
  isShow,
  recipient,
  amount,
  logoUri,
  symbol,
}: {
  isShow: boolean
  recipient: string
  amount: string
  logoUri: string
  symbol: string
}): ReactElement => {
  if (!isShow) {
    return <></>
  }

  return (
    <Box>
      <Typography
        textAlign="left"
        sx={{
          color: 'var(--Ebony-Clay-400, #AAB3CF)',
          fontSize: '14px',
          lineHeight: '160%',
          fontWeight: '400',
          marginBottom: '16px',
        }}
      >
        Please review the information carefully
      </Typography>
      <Grid
        container
        sx={{
          paddingX: '16px',
          paddingY: '16px',
          width: 'auto',
          border: '1px solid var(--Ebony-Clay-950, #2F354B)',
          borderRadius: '6px',
        }}
      >
        <Grid container mb={'16px'}>
          <Grid item xs={3} sm={2}>
            <Typography
              textAlign="left"
              sx={{
                color: 'var(--Ebony-Clay-400, #AAB3CF)',
                fontSize: '14px',
                lineHeight: '160%',
                fontWeight: '400',
              }}
            >
              Network:
            </Typography>
          </Grid>
          <Grid item xs={9} sm={10}>
            <ChainIndicator inline />
          </Grid>
        </Grid>
        <Grid container mb={'16px'}>
          <Grid item xs={3} sm={2}>
            <Typography
              textAlign="left"
              sx={{
                color: 'var(--Ebony-Clay-400, #AAB3CF)',
                fontSize: '14px',
                lineHeight: '160%',
                fontWeight: '400',
              }}
            >
              Send:
            </Typography>
          </Grid>
          <Grid item xs={9} sm={10}>
            {/* <span className={classnames(css.inlineIndicator, css.withLogo)}>
              {amount} {symbol} {logo}
            </span> */}
            <AmountWithIcon uiAmount={amount} symbol={symbol} logoUri={logoUri} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={3} sm={2}>
            <Typography
              textAlign="left"
              sx={{
                color: 'var(--Ebony-Clay-400, #AAB3CF)',
                fontSize: '14px',
                lineHeight: '160%',
                fontWeight: '400',
              }}
            >
              To:
            </Typography>
          </Grid>
          <Grid item xs={9} sm={10}>
            <EthHashInfo
              address={recipient}
              hasExplorer={false}
              showCopyButton
              showName={false}
              shortAddress={false}
              showAvatar={false}
              shortenLen={8}
              showPrefix={false}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default SendAssets
